import { useState, useEffect, useRef } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";
import { v4 as uuid } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Draggable from "react-draggable";
import useMeasure from "react-use-measure";
import clsx from "clsx";

import ModeSelect from "../components/ModeSelect";
import Button from "../components/Button";
import TopBar from "../components/refresh/TopBar";
import { useMediaSelector } from "../components/refresh/selectors/MediaSelector";
import { fetchMedia } from "../../lib/player/src/popups/Content";
import EditHotspot from "../components/hotspots/EditHotspot";
import CreateHotspot from "../components/hotspots/CreateHotspot";
import useHover from "../../lib/player/src/hooks/useHover";
import Checkbox from "../components/refresh/inputs/Checkbox";
import TextInput from "../components/refresh/inputs/TextInput";

const PopupLocation = ({
  point,
  className,
  onChange,
  canvas,
  style,
  ...rest
}) => {
  const [image, setImage] = useState(null);
  const [ref, hovering] = useHover();
  const [showBubble, setShowBubble] = useState(false);
  const timeoutRef = useRef();
  const classes = useStyles();

  const imageId = image?.id;

  useEffect(() => {
    if (point.image && imageId !== point.image) {
      fetchMedia(point.image).then(setImage);
    }
  }, [point.image, imageId]);

  const [x, y] = [point.bubble?.x, point.bubble?.y];

  useEffect(() => {
    setShowBubble(true);
    timeoutRef.current = setTimeout(() => setShowBubble(false), 1000);
    return () => clearTimeout(timeoutRef.current);
  }, [x, y]);

  return (
    <>
      {!point.hideBubble && (
        <div
          className={classes.nextContainer}
          style={{
            top: `${point.y}%`,
            left: `${point.x}%`,
            width: `${point.scale}%`,
            opacity: showBubble ? 1 : 0,
          }}
        >
          <div
            className={classes.next}
            style={{
              top: `${point.bubble?.y ?? 50}%`,
              left: `${point.bubble?.x ?? 50}%`,
            }}
          >
            <div className={classes.ring} />
          </div>
          <img src={image?.public_url} alt="" />
        </div>
      )}
      <Draggable
        bounds="parent"
        position={{
          x: (point.x / 100) * canvas.width,
          y: (point.y / 100) * canvas.height,
        }}
        onChange={onChange}
        onStop={onChange}
      >
        <img
          ref={ref}
          className={clsx(classes.sticker, className)}
          src={image?.public_url}
          onDragStart={e => e.preventDefault()}
          alt=""
          style={{
            ...style,
            width: `${point.scale}%`,
            backgroundColor: hovering ? "rgba(0, 0, 0, 0.5" : "transparent",
            zIndex: 10 + point.y / 10,
          }}
          {...rest}
        />
      </Draggable>
      <div
        className={classes.tooltip}
        style={{
          opacity: hovering ? 1 : 0,
          top: `${point.y}%`,
          left: `calc(${point.x}% + 30px)`,
        }}
      >
        {point.config?.tooltip ?? "No config set"}
      </div>
    </>
  );
};

const PointSettings = ({
  point,
  onChange,
  editConfig,
  resetConfig,
  remove,
}) => {
  const [media, getMedia] = useMediaSelector();
  const [imageData, setImageData] = useState(null);
  const classes = useStyles();

  const mediaId = media?.id;

  useEffect(() => {
    if (mediaId) {
      onChange({ ...point, image: mediaId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  useEffect(() => {
    if (!point.scale) {
      onChange({ ...point, scale: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point.scale]);

  useEffect(() => {
    if (!point.show) {
      onChange({ ...point, show: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point.show]);

  useEffect(() => {
    if (!point.id) {
      onChange({ ...point, id: uuid() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point.id]);

  useEffect(() => {
    if (!point.stage) {
      onChange({ ...point, stage: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point.stage]);

  const hasBubble = !!point.bubble;

  useEffect(() => {
    if (!hasBubble) {
      onChange({ ...point, bubble: { y: 50, x: 50 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBubble]);

  const imageId = imageData?.id;

  useEffect(() => {
    if (point.image && imageId !== point.image) {
      fetchMedia(point.image).then(setImageData);
    }
  }, [point.image, imageId]);

  return (
    <div className={classes.settingsGroup}>
      <p className={classes.hotspotTitle}>
        {point.config ? point.config.tooltip : "No config set"}{" "}
        {Math.round(point.x)}% {Math.round(point.y)}%
      </p>
      <FontAwesomeIcon
        className={classes.icon}
        icon={["fal", "times"]}
        onClick={() => {
          if (window.confirm("Are you sure you want to delete this sticker?")) {
            remove();
          }
        }}
      />
      <label className={classes.label}>
        <div className={classes.buttons}>
          <button onClick={editConfig}>
            {point.config ? "Edit config" : "Set config"}
          </button>
        </div>
      </label>
      {!!point.config && (
        <label className={classes.label}>
          <div className={classes.buttons}>
            <button onClick={resetConfig}>Reset config</button>
          </div>
        </label>
      )}
      <label className={classes.label}>
        <div className={classes.buttons}>
          <button onClick={() => getMedia([1])}>
            Image: {imageData?.title ?? "Pick an image"}
          </button>
        </div>
      </label>
      <label className={classes.label}>
        Size:
        {point.image && (
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={point.scale ?? 10}
            onChange={evt =>
              onChange(
                produce(point, draft => {
                  draft.scale = parseInt(evt.target.value, 10);
                }),
              )
            }
          />
        )}
      </label>
      <Checkbox
        labelClassName={classes.checkbox}
        value={!point.show}
        onChange={() =>
          onChange(
            produce(point, draft => {
              draft.show = !draft.show;
            }),
          )
        }
      >
        Only show image on hover
      </Checkbox>
      <Checkbox
        labelClassName={classes.checkbox}
        value={point.hideBubble}
        onChange={() =>
          onChange(
            produce(point, draft => {
              draft.hideBubble = !draft.hideBubble;
            }),
          )
        }
      >
        Hide next stage bubble
      </Checkbox>
      {!point.hideBubble && (
        <label className={classes.label}>
          Bubble Location:
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={point.bubble?.x ?? 50}
            onChange={evt =>
              onChange(
                produce(point, draft => {
                  draft.bubble.x = parseInt(evt.target.value, 10);
                }),
              )
            }
          />
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={point.bubble?.y ?? 50}
            onChange={evt =>
              onChange(
                produce(point, draft => {
                  draft.bubble.y = parseInt(evt.target.value, 10);
                }),
              )
            }
          />
        </label>
      )}
      <TextInput
        type="number"
        label="Stage:"
        value={point.stage}
        onChange={value =>
          onChange(
            produce(point, draft => {
              draft.stage = parseInt(value, 10);
            }),
          )
        }
      />
    </div>
  );
};

const Hotspots = ({
  tour,
  onUpdateTour,
  onEmbedTour,
  onSaveTour,
  onExit,
  dirty,
  onTourSettings,
}) => {
  const [media, getMedia] = useMediaSelector();
  const [image, setImage] = useState(null);
  const [activePoint, setActivePoint] = useState(null);
  const [activeHotspot, setActiveHotspot] = useState({
    config: { options: {} },
  });
  const [createHotspot, setCreateHotspot] = useState(false);
  const [editHotspot, setEditHotspot] = useState(false);
  const [ref, canvas] = useMeasure();
  const [mapIntro, setMapIntro] = useState(
    tour.data?.options?.mapIntro || {
      show: false,
      image: null,
      points: [],
    },
  );
  const classes = useStyles();

  useEffect(() => {
    if (media && media.id) {
      setMapIntro(h => ({ ...h, image: media.id }));
      setImage(media);
    }
  }, [media]);

  const imageId = image?.id;

  useEffect(() => {
    if (mapIntro.image && imageId !== mapIntro.image) {
      fetchMedia(mapIntro.image).then(setImage);
    }
  }, [mapIntro.image, imageId]);

  const tourJSON = JSON.stringify(tour);
  const mapIntroJSON = JSON.stringify(mapIntro);

  // for saving
  useEffect(() => {
    const tourUnJSON = JSON.parse(tourJSON);
    const mapIntroUnJSON = JSON.parse(mapIntroJSON);
    if (
      JSON.stringify(tourUnJSON.data?.options?.mapIntro || {}) !== mapIntroJSON
    ) {
      onUpdateTour(
        produce(tourUnJSON, draft => {
          draft.data.options.mapIntro = mapIntroUnJSON;
          draft.data.iframeOptions.mapIntro = mapIntroUnJSON;
        }),
      );
    }
  }, [onUpdateTour, tourJSON, mapIntroJSON]);

  if (mapIntro.show) {
    return (
      <div className={classes.container}>
        <TopBar
          links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
        />
        <div className={classes.mainContainer}>
          <div className={classes.options}>
            <div className={classes.label}>
              Disable Map Intro:
              <div className={classes.buttons}>
                <button
                  onClick={() =>
                    setMapIntro(h => ({
                      ...h,
                      show: false,
                    }))
                  }
                >
                  Disable
                </button>
              </div>
            </div>
            <label className={classes.label}>
              Background Image:
              <div className={classes.buttons}>
                <button onClick={() => getMedia([1])}>
                  {image?.title ?? "Pick an image"}
                </button>
              </div>
            </label>
            <hr />
            <div className={classes.buttons}>
              <button
                onClick={() =>
                  setMapIntro(
                    produce(draft => {
                      draft.points.unshift({
                        id: uuid(),
                        x: 0,
                        y: 0,
                        config: null,
                        image: null,
                        scale: 10,
                      });
                    }),
                  )
                }
              >
                Add new hotspot
              </button>
            </div>
            {mapIntro.points.map((point, index) => (
              <PointSettings
                key={point.id ?? index}
                point={point}
                onChange={point =>
                  setMapIntro(
                    produce(draft => {
                      draft.points[index] = point;
                    }),
                  )
                }
                editConfig={() => {
                  setActivePoint(index);
                  if (point.config) {
                    setEditHotspot(true);
                    setActiveHotspot(point);
                  } else {
                    setCreateHotspot(true);
                  }
                }}
                resetConfig={() => {
                  setMapIntro(
                    produce(draft => {
                      delete draft.points[activePoint].config;
                    }),
                  );
                }}
                remove={() =>
                  setMapIntro(
                    produce(draft => {
                      draft.points.splice(index, 1);
                    }),
                  )
                }
              />
            ))}
          </div>

          <div className={classes.mapContainer}>
            {mapIntro.image ? (
              <div className={classes.canvas} ref={ref}>
                {mapIntro.points.map((point, index) => (
                  <PopupLocation
                    key={point.id ?? index}
                    canvas={canvas}
                    onChange={(_, pos) => {
                      setMapIntro(
                        produce(draft => {
                          draft.points[index].x = (pos.x / canvas.width) * 100;
                          draft.points[index].y = (pos.y / canvas.height) * 100;
                        }),
                      );
                    }}
                    point={point}
                  />
                ))}
                {image && (
                  <img
                    onDragStart={e => e.preventDefault()}
                    src={image?.public_url}
                    alt="map"
                    className={classes.map}
                  />
                )}
              </div>
            ) : (
              <p>Select an image</p>
            )}
          </div>
        </div>
        <ModeSelect />
        <CreateHotspot
          show={createHotspot}
          onClose={() => setCreateHotspot(false)}
          onSelect={type => {
            setActiveHotspot({ config: { type, uuid: uuid(), options: {} } });
            setCreateHotspot(false);
            setEditHotspot(true);
          }}
        />
        <EditHotspot
          show={editHotspot}
          hotspot={activeHotspot.config}
          type={activeHotspot.config.type}
          onClose={() => setEditHotspot(false)}
          images={tour.data?.images}
          onComplete={config => {
            setMapIntro(
              produce(draft => {
                draft.points[activePoint].config = config;
              }),
            );
            setEditHotspot(false);
          }}
          account={tour.account}
          event={tour.team}
        />
      </div>
    );
  } else {
    return (
      <div className={classes.container}>
        <TopBar
          links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
        />
        <div className={classes.centerer}>
          <div className={classes.enabledWarning}>
            <h1>Map Intro is not enabled</h1>
            <Button
              onClick={() =>
                setMapIntro(h => ({
                  ...h,
                  show: true,
                }))
              }
            >
              Enable
            </Button>
          </div>
        </div>
        <ModeSelect />
      </div>
    );
  }
};

const useStyles = createUseStyles(theme => ({
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    "flex-direction": "column",
    overflow: "hidden",
  },
  mainContainer: {
    display: "flex",
    flex: 1,
    "flex-direction": "row",
    overflow: "hidden",
    "background-color": "#333",
    height: "100%",
  },
  centerer: {
    "background-color": "#333",
    flex: 1,
    height: "100%",
    display: "flex",
    "justify-content": "center",
    "align-items": "center",
  },
  enabledWarning: {
    display: "flex",
    "flex-direction": "column",
    "align-items": "center",
    "& h1": {
      color: "white",
    },
  },
  options: {
    display: "flex",
    "flex-direction": "column",
    "align-items": "center",
    "overflow-y": "auto",
    "overflow-x": "hidden",
    borderRight: "1px solid #aaa",
    "& hr": {
      width: "90%",
    },
  },
  label: {
    color: "white",
    display: "flex",
    "flex-direction": "column",
    width: 350,
    "flex-shrink": 0,
  },
  buttons: {
    display: "flex",
    "flex-direction": "row",
    margin: [5, 0],
    "& button": {
      background: "#575757",
      color: "white",
      outline: "none",
      border: "none",
      "border-radius": 7,
      padding: "4px 15px",
      cursor: "pointer",
    },
  },
  buttonActive: { background: `${theme.colorSecondary} !important` },
  hotspotPlay: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 27,
    opacity: 0.75,
    color: "white",
    "&:hover": {
      top: 4,
      opacity: 1,
    },
  },
  backgroundHidden: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 9,
  },
  mapContainer: {
    flex: 1,
    padding: 15,

    "& p": {
      color: "white",
    },
  },
  canvas: {
    position: "relative",
    overflow: "hidden",
  },
  map: {
    maxWidth: "100%",
    maxHeight: "100%",
    display: "block",
  },
  inputs: {
    display: "flex",
    flexDirection: "column",
  },
  sticker: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
  },
  tooltip: {
    position: "absolute",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    fontFamily: "DaytonaPro, sans-serif",
    fontSize: 14,
    padding: [3, 10],
    borderRadius: 3,
    transform: "translate(-50%, -50%)",
    transition: "opacity 0.2s ease",
    pointerEvents: "none",
    width: "max-content",
    zIndex: 100000000,
  },
  hotspotTitle: {
    margin: 0,
    color: "white",
    textAlign: "left",
    fontWeight: "bold",
  },
  icon: {
    position: "absolute",
    top: 0,
    right: 5,
    color: "white",
    fontSize: 20,
  },
  settingsGroup: {
    position: "relative",
    border: "1px solid #aaa",
    margin: 5,
    padding: [5, 0, 15],
    borderRadius: 5,

    "& p": {
      marginLeft: 15,
    },

    "& label": {
      padding: [0, 15],
      marginTop: 10,
    },
  },
  nextContainer: {
    position: "absolute",
    overflow: "visible",
    "& img": {
      opacity: 0,
      pointerEvents: "none",
      width: "100%",
    },
  },
  next: {
    position: "absolute",
    transform: "translate(-50%, -50%) scale(0.2)",
    width: 75,
    height: 75,
    borderRadius: "50%",
    backgroundColor: "rgb(255, 188, 13)",
    cursor: "pointer",
    zIndex: 100000000000,
    "&:hover > $ring": {
      animation: "none",
    },
  },
  ring: {
    width: 105,
    height: 105,
    borderRadius: "50%",
    position: "relative",
    top: -25,
    left: -25,
    border: "10px solid rgb(255, 188, 13)",
    animationName: "$pulse",
    animationDuration: "1s",
    animationIterationCount: "infinite",
    pointerEvents: "none",
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 0,
      transform: "scale(0.8)",
    },
    "20%": {
      opacity: 1,
    },
    "40%": {
      opacity: 1,
    },
    "100%": {
      opacity: 0,
      transform: "scale(1.5)",
    },
  },
  location: {},
}));

export default Hotspots;
